import React, { useState, useEffect, useCallback } from 'react';
import Platform from '../utils/platform';
import { Capacitor, registerPlugin } from '@capacitor/core';

interface AppUpdaterPlugin {
    checkForUpdate: () => Promise<{
        updateAvailable: boolean;
        latestVersion?: string;
        latestVersionCode?: number;
        apkUrl?: string;
        releaseNotes?: string;
        error?: string;
    }>;
    downloadAndInstall: (options: { url: string }) => Promise<{ success: boolean }>;
    getCurrentVersion: () => Promise<{ version: string; versionCode: number }>;
    addListener: (event: string, callback: (data: any) => void) => any;
}

const AppUpdater = Capacitor.isNativePlatform()
    ? registerPlugin<AppUpdaterPlugin>('AppUpdater')
    : null;

type UpdateState = 'idle' | 'checking' | 'available' | 'downloading' | 'downloaded' | 'error';

const UpdateBanner: React.FC = () => {
    const [state, setState] = useState<UpdateState>('idle');
    const [version, setVersion] = useState('');
    const [progress, setProgress] = useState(0);
    const [error, setError] = useState('');
    const [dismissed, setDismissed] = useState(false);
    const [apkUrl, setApkUrl] = useState('');

    const checkForUpdate = useCallback(async () => {
        if (Platform.isElectron()) {
            window.electronAPI.updater.checkForUpdates();
        } else if (Platform.isAndroid() && AppUpdater) {
            setState('checking');
            const result = await AppUpdater.checkForUpdate();
            if (result.updateAvailable) {
                setState('available');
                setVersion(result.latestVersion || '');
                setApkUrl(result.apkUrl || '');
            } else {
                setState('idle');
            }
        }
    }, []);

    useEffect(() => {
        if (Platform.isElectron() && window.electronAPI?.updater) {
            window.electronAPI.updater.onUpdateStatus((data) => {
                switch (data.status) {
                    case 'checking':
                        setState('checking');
                        break;
                    case 'available':
                        setState('available');
                        setVersion(data.version || '');
                        setDismissed(false);
                        break;
                    case 'not-available':
                        setState('idle');
                        break;
                    case 'downloading':
                        setState('downloading');
                        setProgress(data.percent || 0);
                        break;
                    case 'downloaded':
                        setState('downloaded');
                        setVersion(data.version || '');
                        break;
                    case 'error':
                        setState('error');
                        setError(data.message || 'Update failed');
                        break;
                }
            });

            return () => {
                window.electronAPI.updater.removeUpdateListeners();
            };
        }

        if (Platform.isAndroid() && AppUpdater) {
            const listener = AppUpdater.addListener('downloadProgress', (data: any) => {
                setState('downloading');
                setProgress(data.percent || 0);
            });

            // Check on mount after 5s delay
            const timer = setTimeout(() => checkForUpdate(), 5000);

            // Check every 30 minutes
            const interval = setInterval(() => checkForUpdate(), 30 * 60 * 1000);

            return () => {
                clearTimeout(timer);
                clearInterval(interval);
                if (listener && listener.remove) listener.remove();
            };
        }
    }, [checkForUpdate]);

    const handleDownload = async () => {
        if (Platform.isElectron()) {
            window.electronAPI.updater.downloadUpdate();
        } else if (Platform.isAndroid() && AppUpdater && apkUrl) {
            setState('downloading');
            setProgress(0);
            await AppUpdater.downloadAndInstall({ url: apkUrl });
        }
    };

    const handleInstall = () => {
        if (Platform.isElectron()) {
            window.electronAPI.updater.installUpdate();
        }
    };

    // Don't show on web
    if (Platform.isWeb()) return null;

    // Nothing to show
    if (state === 'idle' || state === 'checking' || dismissed) return null;

    return (
        <div style={{
            position: 'fixed',
            bottom: 16,
            right: 16,
            zIndex: 99999,
            backgroundColor: state === 'error' ? '#DC2626' : '#1643E0',
            color: '#fff',
            borderRadius: 10,
            padding: '12px 16px',
            fontSize: 13,
            fontWeight: 500,
            fontFamily: 'Inter, sans-serif',
            boxShadow: '0 4px 16px rgba(0,0,0,0.25)',
            minWidth: 260,
            maxWidth: 340,
        }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontWeight: 600 }}>
                    {state === 'available' && `Update ${version} available`}
                    {state === 'downloading' && `Downloading update... ${progress}%`}
                    {state === 'downloaded' && `Update ${version} ready`}
                    {state === 'error' && 'Update failed'}
                </span>
                {(state === 'available' || state === 'error') && (
                    <button
                        onClick={() => setDismissed(true)}
                        style={{
                            background: 'none',
                            border: 'none',
                            color: '#fff',
                            cursor: 'pointer',
                            fontSize: 16,
                            padding: '0 0 0 8px',
                            opacity: 0.7,
                        }}
                    >
                        x
                    </button>
                )}
            </div>

            {state === 'downloading' && (
                <div style={{
                    marginTop: 8,
                    height: 4,
                    backgroundColor: 'rgba(255,255,255,0.3)',
                    borderRadius: 2,
                    overflow: 'hidden',
                }}>
                    <div style={{
                        width: `${progress}%`,
                        height: '100%',
                        backgroundColor: '#fff',
                        borderRadius: 2,
                        transition: 'width 0.3s ease',
                    }} />
                </div>
            )}

            {state === 'available' && (
                <button onClick={handleDownload} style={{
                    marginTop: 8,
                    padding: '6px 16px',
                    backgroundColor: '#fff',
                    color: '#1643E0',
                    border: 'none',
                    borderRadius: 6,
                    fontWeight: 600,
                    fontSize: 12,
                    cursor: 'pointer',
                    width: '100%',
                }}>
                    Download & Install
                </button>
            )}

            {state === 'downloaded' && (
                <button onClick={handleInstall} style={{
                    marginTop: 8,
                    padding: '6px 16px',
                    backgroundColor: '#fff',
                    color: '#1643E0',
                    border: 'none',
                    borderRadius: 6,
                    fontWeight: 600,
                    fontSize: 12,
                    cursor: 'pointer',
                    width: '100%',
                }}>
                    Restart & Update
                </button>
            )}

            {state === 'error' && (
                <div style={{ marginTop: 4, fontSize: 11, opacity: 0.8 }}>{error}</div>
            )}
        </div>
    );
};

export default UpdateBanner;
